import type {
  ChatAskImage,
  ChatAskTurn,
  ChatClient,
  ChatChannelSummary,
  ChatMessagesPage,
  ChatDirectoryPerson,
} from '@ampersandbrands/ui';
import { apiGet, apiPost } from './api';
import { API_BASE } from './apiBase';

const CHAT = `${API_BASE}/v1/chat`;

// Raw image shape the API returns alongside an Ask Amp answer: either a
// candyology entry with a presigned `url`, or a content-attachment ref that
// needs a per-image presign call. The widget only renders resolved
// `{ url, caption }`, so we normalize to that union before returning.
type RawAskImage =
  | { url: string; caption?: string | null }
  | {
      item_id: string;
      attachment_id: string;
      filename?: string | null;
      caption?: string | null;
    };

async function resolveAskImage(raw: RawAskImage): Promise<ChatAskImage> {
  if ('url' in raw) return { url: raw.url, caption: raw.caption ?? null };
  const r = await apiGet<{ url: string }>(
    `${API_BASE}/v1/content/published/${raw.item_id}/attachments/${raw.attachment_id}`,
  );
  return { url: r.url, caption: raw.caption ?? null };
}

// Resolve each image independently; drop any whose presign call fails so one
// broken attachment can't blank the whole answer.
async function resolveAskImages(raws: RawAskImage[] | undefined): Promise<ChatAskImage[] | undefined> {
  const resolved = await Promise.allSettled((raws ?? []).map(resolveAskImage));
  const images = resolved
    .filter((s): s is PromiseFulfilledResult<ChatAskImage> => s.status === 'fulfilled')
    .map((s) => s.value);
  return images.length > 0 ? images : undefined;
}

// The directory is a full org list; fetch once and filter client-side.
let directoryCache: ChatDirectoryPerson[] | null = null;

// Implements the shared ChatWidget's data contract against Sweet Spot's api
// layer. The widget manages its own UI state; this is a thin transport shim.
// `getStoreNo` supplies the caller's effective store (HQ users' header pick or
// the store user's own store) at ask time, so Ask Amp answers use that store's
// inventory; without it HQ users have no store context and Amp reports no
// stock data.
export function createChatClient(getStoreNo?: () => string | number | null | undefined): ChatClient {
  return {
    async listChannels() {
      const r = await apiGet<{ channels: ChatChannelSummary[] }>(`${CHAT}/channels`);
      return r.channels ?? [];
    },
    async listMessages(channelKey, cursor) {
      const qs = cursor != null ? `?cursor=${encodeURIComponent(cursor)}` : '';
      return await apiGet<ChatMessagesPage>(
        `${CHAT}/channels/${encodeURIComponent(channelKey)}/messages${qs}`,
      );
    },
    async sendMessage(channelKey, input) {
      await apiPost(`${CHAT}/channels/${encodeURIComponent(channelKey)}/messages`, input);
    },
    async markRead(channelKey, lastReadId) {
      await apiPost(`${CHAT}/channels/${encodeURIComponent(channelKey)}/read`, {
        last_read_id: lastReadId,
      });
    },
    async searchDirectory(query) {
      if (!directoryCache) {
        const r = await apiGet<{ people: { name: string; email: string }[] }>(
          `${API_BASE}/v1/core/directory`,
        );
        directoryCache = (r.people ?? []).map((p) => ({ email: p.email, name: p.name }));
      }
      const q = query.trim().toLowerCase();
      return directoryCache
        .filter((p) => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
        .slice(0, 25);
    },
    async openDm(email, name) {
      const r = await apiPost<{ channel_key: string }>(`${CHAT}/dm`, { email, name });
      return r.channel_key;
    },
    async askAmp(question, history, storeNoParam) {
      const body: { question: string; history?: ChatAskTurn[]; store_no?: string } = { question };
      if (history && history.length > 0) body.history = history;
      // Sweet Spot doesn't pass `askStores` to the widget, so the widget never
      // supplies `storeNoParam` today; the getter (header pick / mapped store)
      // is the store source. If the widget ever does pass one, it wins.
      const storeNo = storeNoParam ?? getStoreNo?.();
      if (storeNo != null && storeNo !== '') body.store_no = String(storeNo);
      const r = await apiPost<{ answer: string | null; images?: RawAskImage[] }>(
        `${CHAT}/ask`,
        body,
      );
      const images = await resolveAskImages(r.images);
      return { answer: r.answer ?? null, images };
    },
    async askHistory(cursor) {
      const qs = cursor != null ? `?cursor=${encodeURIComponent(cursor)}` : '';
      const r = await apiGet<{
        items: {
          id: number;
          question: string;
          answer: string | null;
          images?: RawAskImage[];
          created_at: string;
        }[];
        next_cursor?: number | null;
      }>(`${CHAT}/ask/history${qs}`);
      // History items carry the same image union the ask endpoint returns;
      // resolve refs per item the same way (drop failures, keep the item).
      // Typed structurally: the ui package declares ChatAskHistoryItem but
      // does not export it (as of 0.4.0).
      const items = await Promise.all(
        (r.items ?? []).map(async (it) => ({
          id: it.id,
          question: it.question,
          answer: it.answer ?? null,
          images: await resolveAskImages(it.images),
          created_at: it.created_at,
        })),
      );
      return { items, next_cursor: r.next_cursor ?? null };
    },
  };
}
